import type { Row, SortingState, Updater } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

import {
  Card,
  TreeTable,
  TreeTableSkeleton,
  useTreeTableInstance,
} from "metabase/ui";
import type { DependencySortOptions } from "metabase-enterprise/dependencies/types";
import { getNodeId } from "metabase-enterprise/dependencies/utils";
import type { DependencyNode } from "metabase-types/api";

import { DiagnosticsEmptyState } from "../DiagnosticsEmptyState";
import type { DependencyDiagnosticsMode } from "../types";

import {
  getColumnWidths,
  getColumns,
  getNotFoundMessage,
  getSortingOptions,
  getSortingState,
} from "./utils";

type DiagnosticsTableProps = {
  nodes: DependencyNode[];
  mode: DependencyDiagnosticsMode;
  sortOptions: DependencySortOptions | undefined;
  isLoading?: boolean;
  onSelect: (node: DependencyNode) => void;
  onSortOptionsChange: (sortOptions: DependencySortOptions | undefined) => void;
};

export const DiagnosticsTable = function DiagnosticsTable({
  nodes,
  mode,
  sortOptions,
  isLoading = false,
  onSelect,
  onSortOptionsChange,
}: DiagnosticsTableProps) {
  const columns = useMemo(() => getColumns(mode), [mode]);
  const sortingState = useMemo(
    () => getSortingState(sortOptions),
    [sortOptions],
  );

  const handleRowActivate = useCallback(
    (row: Row<DependencyNode>) => onSelect(row.original),
    [onSelect],
  );

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSortingState =
        typeof updater === "function" ? updater(sortingState) : updater;
      onSortOptionsChange(getSortingOptions(newSortingState));
    },
    [sortingState, onSortOptionsChange],
  );

  const treeTableInstance = useTreeTableInstance<DependencyNode>({
    data: nodes,
    columns,
    sorting: sortingState,
    manualSorting: true,
    getNodeId: (node) => getNodeId(node.id, node.type),
    onRowActivate: handleRowActivate,
    onSortingChange: handleSortingChange,
  });

  return (
    <Card
      flex="0 1 auto"
      mih={0}
      p={0}
      withBorder
      data-testid="dependency-list"
    >
      {isLoading ? (
        <TreeTableSkeleton columnWidths={getColumnWidths(mode)} />
      ) : (
        <TreeTable
          instance={treeTableInstance}
          emptyState={
            <DiagnosticsEmptyState label={getNotFoundMessage(mode)} />
          }
          onRowClick={handleRowActivate}
        />
      )}
    </Card>
  );
};
